import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../Distributor.module.scss';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { commonActions } from '~/thunks/common/commonSlice';
import { cast, handleApiError, notify } from '~/utils/helper';
import accountApi from '~/apis/account';
import BaseRow from '~/components/common/base-row/BaseRow';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { ITransferDistributor } from '~/utils/interface/account';

const cx = classNames.bind(styles);

type ConfirmDistributorDialogProps = {
  open: boolean;
  alertUsername?: string;
  username?: string;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  setOpenTransferDialog: (open: boolean) => void;
};

const ConfirmDistributorDialog = (props: ConfirmDistributorDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['admin/distributor', 'admin/common']);

  const [agent, setAgent] = useState<ITransferDistributor>();
  const [oldDistributor, setOldDistributor] = useState<ITransferDistributor>();
  const [newDistributor, setNewDistributor] = useState<ITransferDistributor>();

  useEffect(() => {
    if (props.open) {
      (async () => {
        try {
          const response = await accountApi.getTransferDistributorInfo({
            username: cast(props.alertUsername),
            new_distributor_username: props.username,
          });
          setAgent(response.data.agent);
          setOldDistributor(response.data.old_distributor);
          setNewDistributor(response.data.new_distributor);
        } catch (error) {
          handleApiError(error);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleClickConfirmation = async () => {
    dispatch(commonActions.setIsLoading(true));
    try {
      await accountApi.transferDistributor({
        username: cast(agent?.username),
        new_distributor_username: newDistributor?.username,
      });
      notify(
        'success',
        t('transfer.success2', {
          agent: cast(agent?.username),
          old_distributor: oldDistributor?.username,
          new_distributor: newDistributor?.username,
        })
      );
      handleSetOpen(false, false);
      props.setOpenTransferDialog(false);
    } catch (e: any) {
      handleApiError(e);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#005F73] text-center'>{t('transfer.confirm')}</DialogTitle>
        <DialogContent className={cx('transfer-container')}>
          <Box className='text-center'>
            <Typography color={'black'}>{t('transfer.message')}</Typography>
            <Typography color={'black'} className='mb-3.5'>
              {t('transfer.message2')}: {dayjs().format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
          <BaseRow className='gap-3'>
            <Box className='w-1/2'>
              <Typography fontWeight={'bold'} color={'black'}>
                {t('transfer.old')}
              </Typography>
              <Box className='border rounded-[12px] p-2.5 mt-1.5'>
                <Typography color={'black'} fontWeight={'bold'} component={'span'}>
                  {t('transfer.oldDistributor')}:{' '}
                </Typography>
                <Typography component={'span'}>{oldDistributor?.name}</Typography>
              </Box>
            </Box>
            <Box className='w-1/2'>
              <Typography fontWeight={'bold'} color={'black'} component={'span'}>
                {t('transfer.new')}
              </Typography>
              <Box className='border rounded-[12px] p-2.5 mt-1.5'>
                <Typography color={'black'} fontWeight={'bold'} component={'span'}>
                  {t('transfer.newDistributor')}:{' '}
                </Typography>
                <Typography component={'span'}>{newDistributor?.name}</Typography>
              </Box>
            </Box>
          </BaseRow>
        </DialogContent>
        <DialogActions className='justify-evenly'>
          <Button variant='outlined' color='secondary' className='min-w-[125px]' onClick={() => handleSetOpen(false)}>
            {t('cancel', { ns: 'admin/common' })}
          </Button>
          <Button
            variant='contained'
            color='secondary'
            className='min-w-[125px] ml-auto'
            onClick={handleClickConfirmation}
          >
            {t('confirm', { ns: 'admin/common' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(ConfirmDistributorDialog);
